/**
 * React Query hooks for an arche's extra Store listings (ADR-035).
 *
 * Endpoints (all under /contributor/arches/:archeId/listings):
 *   GET    /                      listings, slots, the next slot's price
 *   POST   /icon-upload-url       stage an icon upload
 *   POST   /                      add a listing (may buy a $50 slot)
 *   PATCH  /:listingId            change a live listing
 *   POST   /:listingId/retire     take it out (frees its slot)
 *   POST   /:listingId/restore    put it back (may buy a slot)
 *   PUT    /order                 reorder
 *
 * Every write refreshes the overview: a write can change the slots and the
 * price of the next one, not just the listing it touched.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type CreateStoreListingInput,
  type UpdateStoreListingInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useStoreListings(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.storeListings(archeId),
    queryFn: () => api.getStoreListings(archeId),
    enabled: !!archeId,
    // A 403 is an answer ("not a publisher of this arche"), not a failure to retry.
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });
}

/**
 * Upload an icon file to a staging key the listing write then reads. Returns
 * the key. The browser PUTs straight to storage, like the arche icon picker.
 */
export async function uploadStoreListingIcon(archeId: string, file: File): Promise<string> {
  const { uploadUrl, stagingKey } = await api.getStoreListingIconUploadUrl(archeId, {
    mimeType: file.type,
    sizeBytes: file.size,
  });
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Icon upload failed (status ${response.status})`);
  return stagingKey;
}

function useRefreshOnSuccess(archeId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.contributorArches.storeListings(archeId) });
}

export function useCreateStoreListing(archeId: string) {
  const refresh = useRefreshOnSuccess(archeId);
  return useMutation({
    mutationFn: (input: CreateStoreListingInput) => api.createStoreListing(archeId, input),
    // A refused or re-priced submission changes nothing, but the price may
    // have moved (QUOTE_CHANGED): refresh either way.
    onSettled: refresh,
  });
}

export function useUpdateStoreListing(archeId: string) {
  const refresh = useRefreshOnSuccess(archeId);
  return useMutation({
    mutationFn: ({ listingId, input }: { listingId: string; input: UpdateStoreListingInput }) =>
      api.updateStoreListing(archeId, listingId, input),
    onSuccess: refresh,
  });
}

export function useRetireStoreListing(archeId: string) {
  const refresh = useRefreshOnSuccess(archeId);
  return useMutation({
    mutationFn: (listingId: string) => api.retireStoreListing(archeId, listingId),
    onSuccess: refresh,
  });
}

export function useRestoreStoreListing(archeId: string) {
  const refresh = useRefreshOnSuccess(archeId);
  return useMutation({
    mutationFn: ({
      listingId,
      expectedFeeCents,
    }: {
      listingId: string;
      expectedFeeCents: number;
    }) => api.restoreStoreListing(archeId, listingId, expectedFeeCents),
    onSettled: refresh,
  });
}

/** Take back a submission waiting for a person; a slot it bought is refunded. */
export function useWithdrawStoreListingReview(archeId: string) {
  const refresh = useRefreshOnSuccess(archeId);
  return useMutation({
    mutationFn: (reviewId: string) => api.withdrawStoreListingReview(archeId, reviewId),
    onSettled: refresh,
  });
}

export function useReorderStoreListings(archeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (listingIds: string[]) => api.reorderStoreListings(archeId, listingIds),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.contributorArches.storeListings(archeId), data);
    },
  });
}
